// src/pages/Changes2026.tsx
import React from 'react';
import { Link } from 'react-router-dom';
import SEO, { breadcrumbLd, faqLd } from '../components/common/SEO';
import Hero from '../components/common/Hero';
import Breadcrumb from '../components/common/Breadcrumb';
import Sidebar from '../components/common/Sidebar';
import InContentAd from '../components/ads/InContentAd';
import Icon from '../components/common/Icon';
import {
    changes2026,
    keyFigures2026,
    medianIncome2026,
    benefitThresholds2026,
} from '../data/changes2026';
import { getCategory } from '../data/categories';

const won = (n: number) => n.toLocaleString('ko-KR') + '원';

const faq = [
    {
        q: '2026년 기준 중위소득은 얼마인가요?',
        a: '2026년 기준 중위소득은 1인 가구 2,564,238원, 4인 가구 6,494,738원입니다. 2025년 대비 6.51% 인상되어 역대 최대 인상률을 기록했습니다.',
    },
    {
        q: '2026년 최저임금은 얼마인가요?',
        a: '2026년 적용 최저임금은 시간급 10,320원입니다. 주 40시간(월 209시간) 기준 월 환산액은 2,156,880원이며, 업종 구분 없이 모든 사업장에 동일하게 적용됩니다.',
    },
    {
        q: '2026년 생계급여는 얼마까지 받을 수 있나요?',
        a: '생계급여 선정기준은 기준 중위소득의 32%로, 4인 가구 기준 월 2,078,316원, 1인 가구 기준 월 820,556원입니다. 선정기준이 곧 최저보장수준이며 실제로는 가구의 소득인정액을 뺀 금액을 지급받습니다.',
    },
    {
        q: '기준 중위소득이 오르면 무엇이 달라지나요?',
        a: '기준 중위소득은 생계·의료·주거·교육급여를 포함해 70여 개 복지사업의 선정 기준으로 쓰입니다. 인상되면 선정기준선이 함께 올라 더 많은 가구가 수급 대상이 되고 지급액도 늘어납니다.',
    },
];

const Changes2026: React.FC = () => {
    const crumbs = [
        { name: '홈', path: '/' },
        { name: '2026년 달라지는 정부지원금', path: '/2026' },
    ];

    return (
        <>
            <SEO
                path="/2026"
                title="2026년 달라지는 정부지원금 총정리 - 기준 중위소득·최저임금·생계급여"
                description="2026년 기준 중위소득 4인 6,494,738원(+6.51%), 최저임금 시간급 10,320원, 생계급여 4인 2,078,316원, 기초연금 월 349,700원. 2026년에 확정된 정부지원금 기준 변경 사항을 표와 함께 정리했습니다."
                keywords="2026 기준 중위소득, 2026 최저임금, 2026 생계급여, 2026 주거급여, 2026 기초연금, 2026 정부지원금, 달라지는 제도, 중위소득 표"
                updated="2026-09-02"
                jsonLd={[breadcrumbLd(crumbs), faqLd(faq)]}
            />

            <div className="gh-layout">
                <div>
                    <Breadcrumb items={crumbs} />

                    <Hero
                        eyebrow="2026년 확정 기준"
                        title="2026년, 정부지원금이 이렇게 달라졌습니다"
                        description={
                            <>
                                기준 중위소득이 <strong>6.51%</strong> 올라 역대 최대 인상률을 기록했고,
                                최저임금은 시간급 <strong>10,320원</strong>으로 확정되었습니다.
                            </>
                        }
                        stats={keyFigures2026.slice(0, 3).map(f => ({ value: f.value, label: f.label }))}
                        art="chart"
                    />

                    {/* 핵심 지표 */}
                    <section className="gh-sec">
                        <div className="gh-sec__head">
                            <div>
                                <h2 className="gh-sec__title">
                                    <Icon name="coin" size={19} />
                                    2026년 핵심 지표 한눈에
                                </h2>
                            </div>
                        </div>
                        <div className="gh-budget">
                            {keyFigures2026.map(f => (
                                <div className="gh-budget__item" key={f.label}>
                                    <div className="gh-budget__label">{f.label}</div>
                                    <div className="gh-budget__value">{f.value}</div>
                                    <div className="gh-budget__sub">
                                        {f.sub}
                                        <br />
                                        <span style={{ color: 'var(--emerald)', fontWeight: 700 }}>{f.change}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* 기준 중위소득 표 */}
                    <section className="gh-sec">
                        <div className="gh-sec__head">
                            <div>
                                <h2 className="gh-sec__title">
                                    <Icon name="users" size={19} />
                                    2026년 가구원수별 기준 중위소득
                                </h2>
                                <p className="gh-sec__desc">
                                    보건복지부 고시 기준 · 월 소득 기준 (2025년 대비 6.51% 인상)
                                </p>
                            </div>
                        </div>
                        <div className="gh-tablewrap">
                            <table className="gh-table">
                                <caption className="sr-only">2026년 가구원수별 기준 중위소득</caption>
                                <thead>
                                    <tr>
                                        <th scope="col">가구원수</th>
                                        <th scope="col" className="num">기준 중위소득</th>
                                        <th scope="col" className="num">생계급여 (32%)</th>
                                        <th scope="col" className="num">주거급여 (48%)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {medianIncome2026.map(m => (
                                        <tr key={m.household}>
                                            <th scope="row">{m.household}</th>
                                            <td className="num">{won(m.amount)}</td>
                                            <td className="num accent">{won(Math.floor(m.amount * 0.32))}</td>
                                            <td className="num">{won(Math.floor(m.amount * 0.48))}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </section>

                    {/* 급여별 선정기준 */}
                    <section className="gh-sec">
                        <div className="gh-sec__head">
                            <div>
                                <h2 className="gh-sec__title">
                                    <Icon name="document" size={19} />
                                    급여별 선정기준 (2026년)
                                </h2>
                                <p className="gh-sec__desc">선정기준 비율은 2025년과 동일하게 유지되었습니다</p>
                            </div>
                        </div>
                        <div className="gh-tablewrap">
                            <table className="gh-table">
                                <caption className="sr-only">2026년 급여별 선정기준</caption>
                                <thead>
                                    <tr>
                                        <th scope="col">급여 종류</th>
                                        <th scope="col" className="num">기준 중위소득 대비</th>
                                        <th scope="col" className="num">1인 가구</th>
                                        <th scope="col" className="num">4인 가구</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {benefitThresholds2026.map(b => (
                                        <tr key={b.name}>
                                            <th scope="row">{b.name}</th>
                                            <td className="num">{b.ratio}%</td>
                                            <td className="num">{won(b.onePerson)}</td>
                                            <td className="num accent">{won(b.fourPerson)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </section>

                    <InContentAd />

                    {/* 변경 사항 목록 */}
                    <section className="gh-sec">
                        <div className="gh-sec__head">
                            <div>
                                <h2 className="gh-sec__title">
                                    <Icon name="sparkle" size={19} />
                                    2026년 주요 변경 사항 <em>{changes2026.length}건</em>
                                </h2>
                            </div>
                        </div>

                        <div className="gh-ba">
                            {changes2026.map(c => (
                                <article className="gh-ba__item" key={c.id}>
                                    <h3 className="gh-ba__title">
                                        {c.title}
                                        <span
                                            className="gh-card__cat"
                                            style={{ marginLeft: 8, verticalAlign: 3 }}
                                        >
                                            {getCategory(c.category).shortName}
                                        </span>
                                    </h3>
                                    <div className="gh-ba__row">
                                        <div className="gh-ba__cell gh-ba__cell--before">
                                            <b>2025년</b>
                                            {c.before}
                                        </div>
                                        <Icon name="arrowRight" size={18} className="gh-ba__arrow" />
                                        <div className="gh-ba__cell gh-ba__cell--after">
                                            <b>2026년</b>
                                            {c.after}
                                        </div>
                                    </div>
                                    <p className="gh-ba__detail">{c.detail}</p>
                                    {c.slug && (
                                        <Link
                                            to={`/program/${c.slug}`}
                                            className="gh-card__link"
                                            style={{ marginTop: 10, display: 'inline-flex' }}
                                        >
                                            해당 지원금 자세히 보기
                                            <Icon name="chevronRight" size={14} strokeWidth={2.2} />
                                        </Link>
                                    )}
                                </article>
                            ))}
                        </div>
                    </section>

                    {/* FAQ */}
                    <section className="gh-sec">
                        <div className="gh-sec__head">
                            <h2 className="gh-sec__title">
                                <Icon name="document" size={19} />
                                자주 묻는 질문
                            </h2>
                        </div>
                        <div className="gh-faq">
                            {faq.map((f, i) => (
                                <div className="gh-faq__item is-open" key={i}>
                                    <div className="gh-faq__q" style={{ cursor: 'default' }}>
                                        <span className="gh-faq__mark">Q</span>
                                        {f.q}
                                    </div>
                                    <p className="gh-faq__a">{f.a}</p>
                                </div>
                            ))}
                        </div>
                    </section>

                    <div className="gh-callout">
                        <Icon name="chart" size={20} />
                        <div>
                            <div className="gh-callout__title">2027년에는 또 어떻게 달라질까요?</div>
                            <p className="gh-callout__text">
                                2026년 9월 1일 발표된 2027년 정부 예산안에서 기준 중위소득이 역대 최대인 6.7%
                                인상되고, 혼인·출산지원금이 신설됩니다.{' '}
                                <Link to="/2027" style={{ color: 'var(--blue-700)', fontWeight: 700 }}>
                                    2027년 전망 보기 →
                                </Link>
                            </p>
                        </div>
                    </div>

                </div>

                <Sidebar />
            </div>
        </>
    );
};

export default Changes2026;
